import {inject, Injectable} from "@angular/core";
import {HttpClient, HttpResponse} from "@angular/common/http";
import {OrgDto} from "../dto/org.dto";
import {SearchRequest} from "../dto/search/search-table.dto.req";
import {Observable, tap} from "rxjs";
import {WrapperDto} from "../dto/wrapper.dto";
import {UrlBuilder} from "../item/url.item";
import {map} from "rxjs/operators";
import {SearchInvoiceRes} from "../dto/search/search-invoice.res";
import {VoidDto} from "../dto/void.dto";
import {Additionals} from "../item/additionals";
import {InvoiceDto} from "../dto/document/invoice.dto";
import {InvoicePositionDto} from "../dto/document/invoice-position.dto";
import {InvoiceState} from "../../libs/enum/invoice-state";
import {DocumentContactDto} from "../dto/document-contact.dto";
import {DeliveryNoteDto} from "../dto/delivery-note.dto";
import {DocumentCommentDto} from "../dto/document-comment.dto";
import {SearchCommentRes} from "../dto/search/search-comment.res";
import {ProformaInvoiceDto} from "../dto/proforma-invoice.dto";
import {DocumentPropertyDto} from "../dto/document-property.dto";
import {UrlDto} from "../item/url.dto";

@Injectable({providedIn: 'root'})
export class InvoiceRepo {

  private readonly invoiceSearch: string = '/v1/org/{orgId}/invoice/search';
  private readonly invoiceRead: string = '/v1/org/{orgId}/invoice/{id}';
  private readonly invoiceWrite: string = '/v1/org/{orgId}/invoice';
  private readonly invoiceDelete: string = '/v1/org/{orgId}/invoice/{id}/delete';
  private readonly invoicePropertyWrite: string = '/v1/org/{orgId}/invoice/{id}/property';
  private readonly invoiceCommentWrite: string = '/v1/org/{orgId}/invoice/{id}/comment';
  private readonly invoiceCommentSearch: string = '/v1/org/{orgId}/invoice/comment/search';
  private readonly invoiceChangeState: string = '/v1/org/{orgId}/invoice/{id}/state/{state}';
  private readonly invoicePosWrite: string = '/v1/org/{orgId}/invoice/{id}/position';
  private readonly invoicePosDelete: string = '/v1/org/{orgId}/invoice/{id}/position/{posId}/delete';
  private readonly invoicePosMove: string = '/v1/org/{orgId}/invoice/{id}/position/move/{positionNo}';
  private readonly invoicePosAddArticle: string = '/v1/org/{orgId}/invoice/{id}/position/article/{articleId}';
  private readonly invoiceCreateDeliveryNote: string = '/v1/org/{orgId}/invoice/{id}/media/deliverynote';
  private readonly invoiceCreateProformaInvoice: string = '/v1/org/{orgId}/invoice/{id}/media/proformainvoice';
  private readonly invoiceCreateCancellation: string = '/v1/org/{orgId}/invoice/{id}/cancellation';
  private readonly invoiceCreateDraft: string = '/v1/org/{orgId}/invoice/{id}/draft';
  private readonly invoiceCreatePreview: string = '/v1/org/{orgId}/invoice/{id}/media/preview';
  private readonly invoiceContactRead: string = '/v1/org/{orgId}/invoice/{id}/contact/{contactId}';
  private readonly deliveryNoteContactRead: string = '/v1/org/{orgId}/invoice/{id}/deliverynote/{deliveryNoteId}/contact';
  private readonly proformaInvoiceContactRead: string = '/v1/org/{orgId}/invoice/{id}/proformainvoice/{proformaInvoiceId}/contact';

  private readonly http: HttpClient = inject(HttpClient);

  public getById(org: OrgDto, invoiceId: string, add?: Additionals): Observable<WrapperDto<InvoiceDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.invoiceRead).addAdditionals(add)
      .addParam('orgId', org.id).addParam('id', invoiceId).build();
    return this.http.get<WrapperDto<InvoiceDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public update(org: OrgDto, invoice: InvoiceDto, add?: Additionals): Observable<WrapperDto<InvoiceDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.invoiceWrite).addParam('orgId', org.id).addAdditionals(add).build();
    return this.http.post<WrapperDto<InvoiceDto>>(url.getUrl(), invoice, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public delete(org: OrgDto, invoiceId: string): Observable<WrapperDto<VoidDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.invoiceDelete)
      .addParam('orgId', org.id).addParam('id', invoiceId).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public getContactById(org: OrgDto, invoiceId: string, documentContactId: string): Observable<WrapperDto<DocumentContactDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.invoiceContactRead)
      .addParam('orgId', org.id).addParam('id', invoiceId).addParam('contactId', documentContactId).build();
    return this.http.get<WrapperDto<DocumentContactDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public updateProperty(org: OrgDto, invoiceId: string, property: DocumentPropertyDto): Observable<WrapperDto<DocumentPropertyDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.invoicePropertyWrite)
      .addParam('orgId', org.id).addParam('id', invoiceId).build();
    return this.http.post<WrapperDto<DocumentPropertyDto>>(url.getUrl(), property, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Search
  // ##########################################################################

  public search(org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchInvoiceRes>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.invoiceSearch).addParam('orgId', org.id).build();
    return this.http.post<WrapperDto<SearchInvoiceRes>>(url.getUrl(), request, url.getOptions()).pipe(
      tap((res) => res.dataType = res.dataType === 'SearchResultDto' ? 'SearchInvoiceRes' : res.dataType),
      map((res) => new WrapperDto(res))
    );
  }

  // ##########################################################################
  // #### Comment
  // ##########################################################################

  // noinspection JSUnusedGlobalSymbols
  public updateComment(org: OrgDto, invoiceId: string, comment: DocumentCommentDto): Observable<WrapperDto<DocumentCommentDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.invoiceCommentWrite)
      .addParam('orgId', org.id).addParam('id', invoiceId).build();
    return this.http.post<WrapperDto<DocumentCommentDto>>(url.getUrl(), comment, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public searchComment(org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchCommentRes>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.invoiceCommentSearch).addParam('orgId', org.id).build();
    return this.http.post<WrapperDto<SearchCommentRes>>(url.getUrl(), request, url.getOptions()).pipe(
      tap((res) => res.dataType = res.dataType === 'SearchResultDto' ? 'SearchCommentRes' : res.dataType),
      map((res) => new WrapperDto(res))
    );
  }

  // ##########################################################################
  // #### Special actions
  // ##########################################################################

  public getDeliveryNoteContact(org: OrgDto, invoiceId: string, deliveryNoteId: string): Observable<WrapperDto<DocumentContactDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.deliveryNoteContactRead)
      .addParam('orgId', org.id).addParam('id', invoiceId).addParam('deliveryNoteId', deliveryNoteId).build();
    return this.http.get<WrapperDto<DocumentContactDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public getProformaInvoiceContact(org: OrgDto, invoiceId: string, proformaInvoiceId: string): Observable<WrapperDto<DocumentContactDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.proformaInvoiceContactRead)
      .addParam('orgId', org.id).addParam('id', invoiceId).addParam('proformaInvoiceId', proformaInvoiceId).build();
    return this.http.get<WrapperDto<DocumentContactDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public changeState(org: OrgDto, invoiceId: string, state: InvoiceState, add?: Additionals): Observable<WrapperDto<InvoiceDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.invoiceChangeState).addAdditionals(add)
      .addParam('orgId', org.id).addParam('id', invoiceId).addParam('state', state).build();
    return this.http.post<WrapperDto<InvoiceDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public createDeliveryNote(org: OrgDto, invoiceId: string): Observable<WrapperDto<DeliveryNoteDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.invoiceCreateDeliveryNote)
      .addParam('orgId', org.id).addParam('id', invoiceId).build();
    return this.http.post<WrapperDto<DeliveryNoteDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public createProformaInvoice(org: OrgDto, invoiceId: string): Observable<WrapperDto<ProformaInvoiceDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.invoiceCreateProformaInvoice)
      .addParam('orgId', org.id).addParam('id', invoiceId).build();
    return this.http.post<WrapperDto<ProformaInvoiceDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public createCancellation(org: OrgDto, invoiceId: string): Observable<WrapperDto<InvoiceDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.invoiceCreateCancellation)
      .addParam('orgId', org.id).addParam('id', invoiceId).build();
    return this.http.post<WrapperDto<InvoiceDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public createDraft(org: OrgDto, invoiceId: string): Observable<WrapperDto<InvoiceDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.invoiceCreateDraft)
      .addParam('orgId', org.id).addParam('id', invoiceId).build();
    return this.http.post<WrapperDto<InvoiceDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public createPreview(org: OrgDto, invoiceId: string): Observable<HttpResponse<Blob>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.invoiceCreatePreview)
      .addParam('orgId', org.id).addParam('id', invoiceId)
      .setHttpHeader('Accept', '*/*').deleteHttpHeader('Content-Type').build();
    return this.http.get(url.getUrl(), {responseType: 'blob', observe: 'response', headers: url.getHeaders()});
  }

  // ##########################################################################
  // #### Positions
  // ##########################################################################

  public updatePos(org: OrgDto, invoiceId: string, pos: InvoicePositionDto): Observable<WrapperDto<InvoiceDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.invoicePosWrite).addParam('orgId', org.id).addParam('id', invoiceId).build();
    return this.http.post<WrapperDto<InvoiceDto>>(url.getUrl(), pos, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public deletePos(org: OrgDto, invoiceId: string, pos: InvoicePositionDto): Observable<WrapperDto<InvoiceDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.invoicePosDelete).addParam('orgId', org.id)
      .addParam('id', invoiceId).addParam('posId', pos.id).build();
    return this.http.post<WrapperDto<InvoiceDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public movePos(org: OrgDto, invoiceId: string, pos: InvoicePositionDto, positionTarget: number): Observable<WrapperDto<InvoiceDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.invoicePosMove).addParam('orgId', org.id).addParam('id', invoiceId)
      .addParam('positionNo', String(positionTarget)).build();
    return this.http.post<WrapperDto<InvoiceDto>>(url.getUrl(), pos, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public addArticleAsPos(org: OrgDto, invoiceId: string, articleId: string): Observable<WrapperDto<InvoiceDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.invoicePosAddArticle).addParam('orgId', org.id)
      .addParam('id', invoiceId).addParam('articleId', articleId).build();
    return this.http.post<WrapperDto<InvoiceDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }
}
